import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription, map, switchMap, take } from 'rxjs';
import { BaseComponent } from 'src/app/core/helper/base.component';
import { CountryService } from 'src/app/core/http/country/country.service';
import { OutletExtendedDataDTO } from 'src/app/core/http/outlet/model/outlet';
import { OutletService } from 'src/app/core/http/outlet/outlet.service';
import { OutletRightsDTO } from 'src/app/core/outlet-cache/model/outlet';
import { OutletCacheService } from 'src/app/core/outlet-cache/outlet-cache.service';
import { LanguageService } from 'src/app/core/http/language/language.service';

/** @title navbar */
@Component({
  selector: 'navbar-component',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  fileUploader: boolean = false;
  financingAdmin: boolean = false;
  templateUploader: boolean = false;
  masterData: boolean = false;
  featureAdmin: boolean = false;
  multilingualism: boolean = false;
  adminCountries: string[];
  color: string = 'primary';
  options = {
    fixed: true,
  };
  private navbarSubcription: Subscription = new Subscription();

  constructor(
    private outletCacheService: OutletCacheService,
    private outletService: OutletService,
    private countryService: CountryService,
    private languageService: LanguageService
  ) {
    super();
  }

  ngOnInit(): void {
    this.hasRightsOnRefresh();
    this.outletCacheService.outletSelected$.subscribe(() => {
      this.hasRightsOnRefresh();
    });
  }

  hasRightsOnRefresh() {
    if ('presto-outlet' in sessionStorage) {
      this.isFinancingAdmin();
      this.hasFileUploaderRights();
      this.hasTemplateRights();
      this.hasMasterDataRights();
      this.hasMultilingualism();
      this.hasFeatureAdminRights();
    }
  }

  isFinancingAdmin() {
    const getCountries$ = this.countryService.getUserAdminCountries();
    const getOutlet$ = this.outletService.getSelectedOutletInformation();

    this.navbarSubcription.add(
      getCountries$
        .pipe(
          switchMap((userAdminCountries: string[]) => {
            this.adminCountries = userAdminCountries;
            return getOutlet$;
          }),
          take(1)
        )
        .subscribe((outletExtended: OutletExtendedDataDTO) => {
          let selectedOutletCountry = outletExtended.outlet.country.id;
          this.financingAdmin = this.adminCountries.includes(
            selectedOutletCountry
          );
        })
    );
  }

  hasFileUploaderRights() {
    this.hasRights(['FILE UPLOADER']).subscribe((fileUploader) => {
      this.fileUploader = fileUploader;
    });
  }

  hasTemplateRights() {
    this.hasRights(['TEMPLATE']).subscribe((templateUploader) => {
      this.templateUploader = templateUploader;
    });
  }

  hasMasterDataRights() {
    this.hasRights(['MASTERDATA']).subscribe((masterData) => {
      this.masterData = masterData;
    });
  }

  hasFeatureAdminRights() {
    this.hasRights(['FEATURE_ADMIN']).subscribe((featureAdmin) => {
      this.featureAdmin = featureAdmin;
    });
  }

  hasRights(rightsNeeded: string[]) {
    return this.outletCacheService.getSelectedOutlet().pipe(
      map((selectedOutlet: OutletRightsDTO) => {
        let allow: boolean = false;
        allow =
          selectedOutlet.rights.find((right) =>
            rightsNeeded.includes(right.name)
          ) !== undefined;
        return allow;
      })
    );
  }

  hasMultilingualism() {
    this.navbarSubcription.add(
      this.outletCacheService
        .getSelectedOutlet()
        .pipe(
          switchMap((selectedOutlet: OutletRightsDTO) => {
            return this.languageService.getLanguagesbyCountry(
              selectedOutlet.outlet.country.id
            );
          }),
          take(1)
        )
        .subscribe((languages: string[]) => {
          this.multilingualism = languages.length > 1;
        })
    );
  }

  public override ngOnDestroy(): void {
    if (this.navbarSubcription) {
      this.navbarSubcription.unsubscribe();
    }
  }
}
